import { useEffect, useSyncExternalStore, useCallback, useMemo } from 'react';
import {
  listEntries,
  subscribe,
  replayOutbox,
  discardEntry,
  retryEntry,
  isExhausted,
  type OutboxEntry,
} from '../lib/offlineOutbox';

type OutboxState = {
  entries: OutboxEntry[];
  online: boolean;
  /** Set when IndexedDB itself is unavailable (private mode, storage blocked). */
  storageError: string | null;
};

const EMPTY_STATE: OutboxState = { entries: [], online: true, storageError: null };

let cached: OutboxState = EMPTY_STATE;
let initialized = false;

function refresh(): void {
  listEntries()
    .then((entries) => {
      cached = { ...cached, entries, storageError: null };
      bump();
    })
    .catch((e: unknown) => {
      cached = { ...cached, storageError: e instanceof Error ? e.message : String(e) };
      bump();
    });
}

const externalListeners = new Set<() => void>();
function bump(): void {
  for (const l of externalListeners) l();
}

function ensureInit(): void {
  if (initialized) return;
  initialized = true;
  cached = {
    entries: [],
    online: typeof navigator !== 'undefined' ? navigator.onLine : true,
    storageError: null,
  };
  refresh();

  if (typeof window !== 'undefined') {
    const onOnline = () => {
      cached = { ...cached, online: true };
      bump();
      refresh();
    };
    const onOffline = () => {
      cached = { ...cached, online: false };
      bump();
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    subscribe(refresh);
  }
}

function getSnapshot(): OutboxState {
  return cached;
}

function getServerSnapshot(): OutboxState {
  return EMPTY_STATE;
}

function subscribeReact(listener: () => void): () => void {
  externalListeners.add(listener);
  return () => externalListeners.delete(listener);
}

/**
 * Reactive view of the offline outbox.
 *
 * `pending` entries still have retries left and replay automatically on
 * reconnect; `failed` entries hit the retry cap and wait for the technician
 * to retry or discard them.
 */
export function useOfflineOutbox() {
  useEffect(() => {
    ensureInit();
  }, []);

  const state = useSyncExternalStore(subscribeReact, getSnapshot, getServerSnapshot);

  const { pending, failed } = useMemo(() => {
    const pendingEntries: OutboxEntry[] = [];
    const failedEntries: OutboxEntry[] = [];
    for (const entry of state.entries) {
      (isExhausted(entry) ? failedEntries : pendingEntries).push(entry);
    }
    return { pending: pendingEntries, failed: failedEntries };
  }, [state.entries]);

  const flush = useCallback(async () => replayOutbox(), []);
  const discard = useCallback(async (id: string) => discardEntry(id), []);
  const retry = useCallback(async (id: string) => retryEntry(id), []);

  return {
    entries: state.entries,
    pending,
    failed,
    queueDepth: pending.length,
    online: state.online,
    storageError: state.storageError,
    flush,
    discard,
    retry,
  };
}
